import { Component, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * Error Boundary Component
 *
 * Catches React errors in the component tree and displays a fallback UI
 * instead of crashing the entire application.
 *
 * @see https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console for debugging (intentional for error tracking)
    console.error("Error caught by boundary:", error, errorInfo);

    // TODO: Send error to logging service (e.g., Sentry, LogRocket)
    // logErrorToService(error, errorInfo);
  }

  override render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-[var(--color-background)] p-4">
          <div className="max-w-md w-full text-center">
            {/* Error Icon */}
            <div className="text-6xl mb-6">⚠️</div>

            {/* Error Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-text-primary mb-4">
              Qualcosa è andato storto
            </h1>

            {/* Error Message */}
            <p className="text-lg text-text-secondary mb-8">
              Ci scusiamo per l'inconveniente. Si è verificato un errore imprevisto.
            </p>

            {/* Error Details (only in development) */}
            {process.env.NODE_ENV === "development" && this.state.error && (
              <div className="mb-8 p-4 bg-[var(--color-error)]/10 border border-[var(--color-error)] rounded-lg text-left">
                <p className="text-sm font-mono text-error break-all">
                  {this.state.error.toString()}
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                type="button"
                onClick={() => {
                  window.location.href = "/";
                }}
                className="px-6 py-3 bg-[var(--color-primary)] text-white rounded-lg font-semibold hover:bg-[var(--color-primary-dark)] transition-colors duration-[var(--transition-base)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:ring-offset-2"
              >
                Torna alla Home
              </button>
              <button
                type="button"
                onClick={() => {
                  window.location.reload();
                }}
                className="px-6 py-3 bg-[var(--color-surface-elevated)] text-text-primary rounded-lg font-semibold hover:bg-[var(--color-border)] transition-colors duration-[var(--transition-base)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:ring-offset-2"
              >
                Ricarica la pagina
              </button>
            </div>

            {/* Help Text */}
            <p className="mt-8 text-sm text-text-tertiary">
              Se il problema persiste, ti preghiamo di{" "}
              <a href="/contatti" className="text-primary hover:underline">
                contattarci
              </a>
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
